import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ENTREGAS_ACCESS_ROLES, REPORTS_ACCESS_ROLES, ROTAS_ACCESS_ROLES } from '../../constants/roles';
import {
    SidebarBackdrop,
    SidebarContainer,
    Menu,
    MenuItem,
    MenuItemCornerClock,
    LogoutButton,
} from './styles';
import {
    HomeIcon,
    ProfileIcon,
    DeliveryIcon,
    RotasIcon,
    RoteirosIcon,
    CollaboratorsIcon,
    ReportsIcon,
    InvoicesIcon,
    ClientsIcon,
    VehicleIcon,
    SettingsIcon,
    LogoutIcon,
    ClockIcon,
} from './icons';

const menuItems = [
    { label: 'Dashboard', roles: null, icon: <HomeIcon />, path: '/dashboard' },
    { label: 'Perfil do usuário', roles: null, icon: <ProfileIcon />, path: '/perfil' },
    { label: 'Entregas', roles: ENTREGAS_ACCESS_ROLES, icon: <DeliveryIcon />, path: '/entregas' },
    { label: 'Rotas', roles: ROTAS_ACCESS_ROLES, icon: <RotasIcon />, path: '/rotas' },
    { label: 'Roteiros', roles: ['ROLE_DRIVER'], icon: <RoteirosIcon />, path: '/roteiros' },
    { label: 'Colaboradores', roles: ['ROLE_TRANSPORT_COMPANY', 'ROLE_HR_EXTERNAL_EMPLOYEE', 'ROLE_LEGAL_PERSON'], icon: <CollaboratorsIcon />, path: '/colaboradores' },
    { label: 'Relatórios', roles: REPORTS_ACCESS_ROLES, icon: <ReportsIcon />, path: '/relatorios' },
    {
        label: 'Faturas',
        roles: ['ROLE_TRANSPORT_COMPANY', 'ROLE_HR_EMPLOYEE'],
        icon: <InvoicesIcon />,
        path: '/faturas',
    },
    { label: 'Clientes', roles: ['ROLE_TRANSPORT_COMPANY'], icon: <ClientsIcon />, path: '/clientes' },
    { label: 'Veículos', roles: ['ROLE_TRANSPORT_COMPANY'], icon: <VehicleIcon />, path: '/veiculos' },
    { label: 'Configurações', roles: ['ROLE_TRANSPORT_COMPANY'], icon: <SettingsIcon />, path: '/configuracoes' },
];

export default function Sidebar({ asDrawer = false, open = false, onClose }) {
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const userRoles = user?.roles || [];

    const hasRole = (roles) => {
        if (!roles) return true;
        return roles.some((role) => userRoles.includes(role));
    };

    useEffect(() => {
        if (!asDrawer || !open) return undefined;

        const handleEscape = (event) => {
            if (event.key === 'Escape') onClose?.();
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [asDrawer, open, onClose]);

    const handleNavigate = (path) => {
        navigate(path);
        onClose?.();
    };

    const handleLogout = () => {
        onClose?.();
        logout();
    };

    const sidebarContent = (
        <SidebarContainer $drawer={asDrawer} $open={open}>
            <Menu>
                {menuItems.map((item, index) => (
                    hasRole(item.roles) && (
                        <MenuItem
                            key={index}
                            as="button"
                            type="button"
                            title={item.disabled ? item.disabledTitle : item.label}
                            $disabled={item.disabled}
                            aria-disabled={item.disabled ? true : undefined}
                            tabIndex={item.disabled ? -1 : undefined}
                            onClick={() => {
                                if (item.disabled) return;
                                handleNavigate(item.path);
                            }}
                        >
                            {item.icon}
                            {item.disabled && (
                                <MenuItemCornerClock aria-hidden>
                                    <ClockIcon />
                                </MenuItemCornerClock>
                            )}
                        </MenuItem>
                    )
                ))}
            </Menu>
            <LogoutButton onClick={handleLogout} title="Sair">
                <LogoutIcon />
            </LogoutButton>
        </SidebarContainer>
    );

    if (asDrawer) {
        if (!open) return null;
        return (
            <>
                <SidebarBackdrop $open={open} onClick={onClose} aria-hidden />
                {sidebarContent}
            </>
        );
    }

    return sidebarContent;
}
